import { Routes } from '@packages/enums';
import { withSuspense } from '@ui/components/layout/LazyPage';
import { lazy } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router';
import ProjectCrumb from '../../features/PatientRegistry/components/Breadcrumbs/ProjectCrumb';
import CaseCrumb from '../../features/PatientRegistry/components/Breadcrumbs/CaseCrumb';

const Settings = lazy(() => import('../../pages/Settings'));
const RegistryStandards = lazy(() => import('../../pages/RegistryStandards'));
const StandardDetailPage = lazy(() => import('../../pages/RegistryStandards/StandardDetailPage'));
const VersionDetailPage = lazy(() => import('../../pages/RegistryStandards/VersionDetailPage'));
const FieldDetailPage = lazy(() => import('../../pages/RegistryStandards/FieldDetailPage'));
const RulesetDetailPage = lazy(() => import('../../pages/RegistryStandards/RulesetDetailPage'));

const Projects = lazy(() => import('../../pages/PatientRegistry/Projects'));
const ProjectsList = lazy(() => import('../../features/PatientRegistry/components/ProjectsList'));
const ProjectDetails = lazy(() => import('../../features/PatientRegistry/components/ProjectDetails'));
const CaseExtractionResult = lazy(() => import('../../features/PatientRegistry/components/CaseExtractionResult'));
const CreateProject = lazy(() => import('../../features/PatientRegistry/components/CreateProject'));
const EditProject = lazy(() => import('../../features/PatientRegistry/components/EditProject'));
const ImportProject = lazy(() => import('../../features/PatientRegistry/components/ImportProject'));
const Integrations = lazy(() => import('../../pages/PatientRegistry/Integrations'));
const ReportingSourcesList = lazy(() => import('../../features/PatientRegistry/components/ReportingSourcesList'));
const CreateReportingSource = lazy(() => import('../../features/PatientRegistry/components/CreateReportingSource'));
const CaseFinding = lazy(() => import('../../pages/PatientRegistry/CaseFinding'));

export const patientRegisteryRoutes: RouteObject[] = [
    {
        path: Routes.HOME,
        element: (
            <Navigate
                to="/projects"
                replace
            />
        ),
    },
    {
        path: '/projects',
        handle: { crumb: 'Projects' },
        element: withSuspense(Projects),
        children: [
            {
                index: true,
                element: withSuspense(ProjectsList),
            },
            {
                path: ':projectId',
                handle: { crumb: <ProjectCrumb />, crumbText: 'Project' },
                element: <Outlet />,
                children: [
                    {
                        index: true,
                        element: withSuspense(ProjectDetails),
                    },
                    {
                        path: 'cases/:caseId',
                        handle: { crumb: <CaseCrumb />, crumbText: 'Case' },
                        element: <Outlet />,
                        children: [
                            {
                                path: 'extraction-result',
                                handle: { hideProjectsHeader: true },
                                element: withSuspense(CaseExtractionResult),
                            },
                        ],
                    },
                    {
                        path: 'edit',
                        handle: { crumb: 'Update Project' },
                        element: withSuspense(EditProject),
                    },
                ],
            },
            {
                path: 'create',
                handle: { crumb: 'Create Project' },
                element: withSuspense(CreateProject),
            },
            {
                path: 'import',
                handle: { crumb: 'Import Project' },
                element: withSuspense(ImportProject),
            },
        ],
    },
    {
        path: '/integrations',
        handle: { crumb: 'Integrations' },
        element: withSuspense(Integrations),
        children: [
            {
                index: true,
                element: withSuspense(ReportingSourcesList),
            },
            {
                path: 'create',
                handle: { crumb: 'Add Integration' },
                element: withSuspense(CreateReportingSource),
            },
        ],
    },
    {
        path: '/case-finding',
        handle: { crumb: 'Case Finding' },
        element: withSuspense(CaseFinding),
    },
    {
        path: Routes.SETTINGS,
        handle: { crumb: 'Settings' },
        element: withSuspense(Settings),
    },
    {
        path: Routes.REGISTRY_STANDARDS,
        handle: { crumb: 'Registry Standards' },
        element: withSuspense(RegistryStandards),
        children: [
            {
                path: 'versions/:versionId',
                handle: { crumb: 'Version Detail' },
                element: withSuspense(VersionDetailPage),
            },
            {
                path: 'fields/:fieldId',
                handle: { crumb: 'Field Detail' },
                element: withSuspense(FieldDetailPage),
            },
            {
                path: 'clinical-rulesets/:id',
                handle: { crumb: 'Ruleset Detail' },
                element: withSuspense(RulesetDetailPage),
            },
            {
                path: ':id',
                handle: { crumb: 'Standard Detail' },
                element: withSuspense(StandardDetailPage),
            },
        ],
    },
];
